import React, { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';

/**
 * PageTransition - Transitions de page conformes aux principes du skill Motion Design
 * 
 * Principes appliqués:
 * - 3 Motion Layers:
 *   1. Primaire: Translation Y (24px -> 0) & Opacité (0 -> 1)
 *   2. Secondaire: Effet d'échelle & profondeur (0.985 -> 1)
 *   3. Ambiant: Barre d'accentuation dynamique en haut de l'écran
 * - Durée optimale (450ms) recommandée pour le changement de contexte
 * - Courbe d'assouplissement (cubic-bezier 0.2, 0, 0, 1 / MD3) pour une décélération fluide
 * - Prise en charge de prefers-reduced-motion pour l'accessibilité
 */
export default function PageTransition({ children }) {
  const location = useLocation();
  const pageRef = useRef(null);
  const progressLineRef = useRef(null);

  useGSAP(
    () => {
      // Respect des préférences d'accessibilité (motion réducteur)
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      const node = pageRef.current;
      const progressLine = progressLineRef.current;
      if (!node) return;

      const tl = gsap.timeline();

      // Couche Ambiante: Ligne d'accentuation en haut lors du switch de page
      if (progressLine) {
        tl.fromTo(
          progressLine,
          { scaleX: 0, opacity: 1, transformOrigin: 'left center' },
          { scaleX: 1, opacity: 0, duration: 0.45, ease: 'power2.inOut' },
          0
        );
      }

      // Couches Primaire & Secondaire: Élévation Y + Échelle de profondeur
      tl.fromTo(
        node,
        {
          opacity: 0,
          y: 24,
          scale: 0.985,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.45,
          ease: 'cubic-bezier(0.2, 0, 0, 1)',
          clearProps: 'transform,opacity',
        },
        0
      );
    },
    { scope: pageRef, dependencies: [location.pathname] }
  );

  return (
    <div className="relative w-full">
      {/* Ambient Motion Layer: Ligne de progression/transition au sommet */}
      <div
        ref={progressLineRef}
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-acem-primary via-acem-accent to-acem-primary z-50 pointer-events-none opacity-0"
      />
      
      {/* Content Layer */}
      <div ref={pageRef} className="w-full">
        {children}
      </div>
    </div>
  );
}
